import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, endpoints, joinUrl } from '../api/config';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonIcon from '@mui/icons-material/Person';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { BUTTON_STYLE } from '../constants/styles';
import jyBlackLogo from '../assets/JY BLACK.png';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotValue, setForgotValue] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  // Clear any existing authentication when manually navigating to login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isManualNavigation = urlParams.get('manual') === 'true' || !urlParams.has('manual');
    
    if (isManualNavigation) {
      // Clear all authentication data for manual login
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tokenCreationTime');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('twoFactorVerified');
      sessionStorage.removeItem('intermediateFeatureCompleted');
      sessionStorage.removeItem('tempPassword');
    }
  }, []);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (loading) return; // Prevent multiple submissions
    
    setError('');
    setLoading(true);

    if (!username || !password) {
      setError('Please enter both username and password');
      setLoading(false);
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await api.post(endpoints.login, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      });

      if (response.data.access_token) {
        // Store the token immediately so it is preserved during setup/verification
        sessionStorage.setItem('token', response.data.access_token);
        sessionStorage.setItem('tokenCreationTime', Date.now().toString());

        // Check if user is a super admin (for features, not bypass)
        try {
          const superAdminResponse = await axios.post(joinUrl(import.meta.env.VITE_API_URL, endpoints.superAdminStatusCheck), {
            username: username
          });
          
          const isSuperAdmin = superAdminResponse.data.data?.is_super_admin;
          
          // Store super admin status for features, but don't bypass 2FA
          if (isSuperAdmin === true) {
            sessionStorage.setItem('isSuperAdmin', 'true');
          } else {
            sessionStorage.removeItem('isSuperAdmin'); // Ensure regular admins don't have super admin status
          }
        } catch (superAdminErr) {
          console.error('Error checking super admin status:', superAdminErr);
          // If super admin check fails, assume regular admin
          sessionStorage.removeItem('isSuperAdmin');
        }
        
        // For ALL admins (both regular and super), ALWAYS check 2FA status every time (mandatory)
        // Clear any previous 2FA completion status to force fresh check
        sessionStorage.removeItem('twoFactorVerified');
        sessionStorage.removeItem('intermediateFeatureCompleted');
          
          // Check if user has 2FA enabled
          try {
            const twoFactorResponse = await axios.post(joinUrl(import.meta.env.VITE_API_URL, "/auth/check-2fa-status"), {
              username: username
            });
            
            const twoFactorEnabled = twoFactorResponse.data.data?.two_factor_enabled;
            
            if (twoFactorEnabled === true) {
              // User has 2FA enabled, store username and password but don't authenticate yet
              sessionStorage.setItem('username', username);
              sessionStorage.setItem('tempPassword', password); // Temporarily store password for 2FA
              // Don't call login() here - wait for 2FA verification
              navigate('/2fa-login', { replace: true });
            } else if (twoFactorEnabled === false) {
              // User doesn't have 2FA enabled, proceed to 2FA setup
              sessionStorage.setItem('username', username);
              sessionStorage.setItem('tempPassword', password); // Temporarily store password for 2FA setup
              // Don't call login() here - wait for 2FA setup completion
              navigate('/2fa-setup', { replace: true }); // Use specific path for 2FA setup
            } else {
              // If 2FA status is unclear, default to 2FA setup for security
              sessionStorage.setItem('username', username);
              sessionStorage.setItem('tempPassword', password);
              navigate('/2fa-setup', { replace: true });
            }
          } catch (twoFactorErr) {
            console.error('Error checking 2FA status:', twoFactorErr);
            // If 2FA check fails, default to 2FA setup for security
            sessionStorage.setItem('username', username);
            sessionStorage.setItem('tempPassword', password);
            navigate('/2fa-setup', { replace: true });
          }

      } else {
        setError('Invalid response from server');
        setPassword('');
      }
    } catch (err) {
      console.error('Login error:', err);
      setPassword(''); // Clear password on error
      
      // Handle different types of errors
      if (err.response?.status === 504) {
        setError('Gateway timeout. The server is taking too long to respond. Please try again.');
      } else if (err.response?.status === 503) {
        setError('Service temporarily unavailable. Please try again in a few moments.');
      } else if (err.response?.status === 401) {
        // Check if the error is from an invalid login attempt
        const errorDetail = err.response?.data?.detail;
        if (errorDetail?.toLowerCase().includes('incorrect') || 
            errorDetail?.toLowerCase().includes('invalid') ||
            !errorDetail) {
          setError('Invalid username or password');
        } else {
          // Only show expired session message if coming from an expired session and error indicates session expiry
          const searchParams = new URLSearchParams(window.location.search);
          const isExpiredSession = searchParams.get('expired') === 'true' && 
                                 errorDetail?.toLowerCase().includes('expired');
          
          if (isExpiredSession) {
            setError('Your session has expired. Please log in again.');
          } else {
            setError('Invalid username or password');
          }
        }
      } else if (!err.response) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again in a few moments.');
      } else {
        setError(err.response?.data?.detail || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handler
  const handleForgotSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);
    if (!forgotValue) {
      setForgotError('Please enter your email or username');
      setForgotLoading(false);
      return;
    }
    try {
      // Use correct endpoint and attach base URL
      const trimmedValue = forgotValue.trim();
      const payload = { email_or_username: trimmedValue };
      console.log('Forgot password payload:', payload);
      const response = await api.post(
        joinUrl(import.meta.env.VITE_API_URL, '/request-password-reset'),
        payload
      );
      if (response.data && (response.data.success || response.data.status === 'ok' || response.data.message)) {
        setForgotSuccess(response.data.message || 'If an account exists, a reset email has been sent.');
      } else {
        setForgotSuccess('If an account exists, a reset email has been sent.');
      }
    } catch (err) {
      // Handle API error details (array or string)
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // If detail is an array of error objects, join their messages
        setForgotError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      } else if (typeof detail === 'string') {
        setForgotError(detail);
      } else {
        setForgotError('Failed to send reset email.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e3f0ff 0%, #fafcff 100%)',
        position: 'relative',
      }}
    >

      <Container maxWidth="xs">
        <Paper
          elevation={6}
          sx={{
            p: 5,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'white',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          }}
        >
          <img src={jyBlackLogo} alt="JY Logo" style={{ width: 90, paddingBottom: 20 }} />
          <Typography component="h1" variant="h5" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
            JONATHAN Y
          </Typography>
          <Box 
            component="form" 
            onSubmit={handleSubmit} 
            noValidate 
            sx={{ width: '100%', mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              sx={{ 
                backgroundColor: 'background.paper',
                '& .MuiInputBase-root': {
                  transition: 'none'
                },
                '& .MuiOutlinedInput-root': {
                  transition: 'none'
                },
                '& .MuiInputLabel-root': {
                  transition: 'none'
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              sx={{ 
                backgroundColor: 'background.paper',
                '& .MuiInputBase-root': {
                  transition: 'none'
                },
                '& .MuiOutlinedInput-root': {
                  transition: 'none'
                },
                '& .MuiInputLabel-root': {
                  transition: 'none'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={BUTTON_STYLE}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3,
                mb: 2,
                py: 1.5, 
                fontWeight: 600, 
                fontSize: 16, 
                letterSpacing: 1,
                ...BUTTON_STYLE,
                '& .signing-in': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  animation: 'fadeInOut 1s ease-in-out infinite',
                  '@keyframes fadeInOut': {
                    '0%': {
                      opacity: 0.3,
                    },
                    '50%': {
                      opacity: 1,
                    },
                    '100%': {
                      opacity: 0.3,
                    },
                  },
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <span className="signing-in">
                  <CircularProgress size={20} color="inherit" aria-label="Signing in" />
                  Signing in...
                </span>
              ) : 'Sign In'}
            </Button>
            <Button
              variant="text"
              fullWidth
              size="small"
              sx={{
                mb: 1,
                textTransform: 'none',
                fontWeight: 400,
                fontSize: 13,
                color: 'text.secondary',
                minHeight: 'auto',
                py: 0.5,
                px: 0,
                transition: 'color 0.2s, text-decoration 0.2s',
                '&:hover': {
                  color: 'text.primary',
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}
              onClick={() => {
                setForgotOpen(true);
                setForgotValue('');
                setForgotError('');
                setForgotSuccess('');
              }}
              disabled={loading}
            >
              Forgot Password?
            </Button>
          </Box>
        </Paper>
      </Container>
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email or username. If an account exists, you will receive a password reset email.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="forgot-identifier"
            label="Email or Username"
            type="text"
            fullWidth
            value={forgotValue}
            onChange={e => setForgotValue(e.target.value)}
            disabled={forgotLoading}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleForgotSubmit();
              }
            }}
          />
          {forgotError && (
            Array.isArray(forgotError) ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {forgotError.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mt: 2 }}>{forgotError}</Alert>
            )
          )}
          {forgotSuccess && (
            forgotSuccess.toLowerCase().includes('account does not exist') ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                <span role="img" aria-label="cross">❌</span> {forgotSuccess}
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mt: 2 }}>{forgotSuccess}</Alert>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)} disabled={forgotLoading}>Cancel</Button>
          <Button onClick={handleForgotSubmit} variant="contained" disabled={forgotLoading}>
            {forgotLoading ? <CircularProgress size={20} /> : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login; 
